import { useMemo, useState } from 'react';
import { Card, Col, Empty, List, Row, Segmented, Space, Spin, Statistic, Tag, Typography } from 'antd';
import {
  DollarOutlined,
  WarningOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { useOwnerDashboard, useRecentOperations, useSalesTrend } from '@hooks/useAnalytics';

interface OwnerDashboardPageProps {
  onNavigate?: (acqModule: string) => void;
}

// ── Брендові кольори (узгоджені з ТЗ редизайну) ─────────────────────────
const ACQ_COLOR_PRIMARY = '#534AB7';
const ACQ_COLOR_PRIMARY_LIGHT = '#AFA9EC';
const ACQ_COLOR_SUCCESS = '#1D9E75';
const ACQ_COLOR_DANGER = '#E24B4A';
const ACQ_COLOR_WARNING = '#BA7517';

const ACQ_PERIOD_OPTIONS = [
  { label: 'Місяць', value: 1 },
  { label: 'Квартал', value: 3 },
  { label: 'Півроку', value: 6 },
  { label: 'Рік', value: 12 },
];

export default function OwnerDashboardPage({ onNavigate }: OwnerDashboardPageProps) {
  const [acqMonths, setAcqMonths] = useState<number>(6);
  const acqKpiQ = useOwnerDashboard();
  const acqTrendQ = useSalesTrend(acqMonths);
  const acqOpsQ = useRecentOperations(10);

  const acqHandleCard = (acqModule: string) => onNavigate?.(acqModule);

  const acqIsLoading = acqKpiQ.isLoading;
  const acqKpi = acqKpiQ.data ?? {
    totalRevenue: 0,
    totalRevenueLastMonth: 0,
    revenueGrowthPercent: 0,
    grossMargin: 0,
    totalReceivables: 0,
    overdueReceivables: 0,
  };

  const acqGrowthColor = acqKpi.revenueGrowthPercent >= 0 ? ACQ_COLOR_SUCCESS : ACQ_COLOR_DANGER;
  const acqGrowthIcon = acqKpi.revenueGrowthPercent >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />;

  const acqChartOption = useMemo(() => {
    const acqData = acqTrendQ.data ?? [];
    const acqLastMonth = acqData[acqData.length - 1]?.month;
    return {
      grid: { left: 50, right: 16, top: 16, bottom: 28 },
      xAxis: {
        type: 'category',
        data: acqData.map((p) => dayjs(`${p.month}-01`).format('MMM')),
        axisLine: { lineStyle: { color: '#d9d9d9' } },
        axisLabel: { color: '#8c8c8c', fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: '#8c8c8c',
          fontSize: 11,
          formatter: (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`),
        },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: { name: string; value: number }[]) =>
          `<b>${params[0].name}</b><br/>₴ ${params[0].value.toLocaleString('uk-UA')}`,
      },
      series: [
        {
          type: 'bar',
          data: acqData.map((p) => ({
            value: p.revenue,
            itemStyle: {
              color: p.month === acqLastMonth ? ACQ_COLOR_PRIMARY : ACQ_COLOR_PRIMARY_LIGHT,
              borderRadius: [4, 4, 0, 0],
            },
          })),
          barWidth: '55%',
        },
      ],
    };
  }, [acqTrendQ.data]);

  if (acqIsLoading) {
    return <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>;
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {/* Заголовок + селектор періоду */}
      <Row align="bottom" justify="space-between">
        <Col>
          <Typography.Title level={4} style={{ margin: 0 }}>Дашборд</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Огляд за {dayjs().format('MMMM YYYY')}
          </Typography.Text>
        </Col>
        <Col>
          <Segmented
            options={ACQ_PERIOD_OPTIONS}
            value={acqMonths}
            onChange={(v) => setAcqMonths(v as number)}
          />
        </Col>
      </Row>

      {/* 4 KPI */}
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} md={6}>
          <Card
            hoverable
            size="small"
            onClick={() => acqHandleCard('rpt-trial-balance')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="Виручка"
              value={acqKpi.totalRevenue}
              precision={0}
              prefix={<DollarOutlined />}
              suffix="UAH"
            />
            <div style={{ marginTop: 8, color: acqGrowthColor, fontSize: 12 }}>
              {acqGrowthIcon} {acqKpi.revenueGrowthPercent >= 0 ? '+' : ''}
              {acqKpi.revenueGrowthPercent.toFixed(1)}% до попереднього місяця
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable size="small" onClick={() => acqHandleCard('rpt-trial-balance')} style={{ cursor: 'pointer' }}>
            <Statistic
              title="Маржа"
              value={acqKpi.grossMargin}
              precision={1}
              prefix={<RiseOutlined />}
              suffix="%"
              valueStyle={{ color: acqKpi.grossMargin >= 20 ? ACQ_COLOR_SUCCESS : ACQ_COLOR_DANGER }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              Виручка – собівартість
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable size="small" onClick={() => acqHandleCard('acc-counterparties')} style={{ cursor: 'pointer' }}>
            <Statistic
              title="Дебіторка"
              value={acqKpi.totalReceivables}
              precision={0}
              prefix={<DollarOutlined />}
              suffix="UAH"
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              Заборгованість покупців
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card hoverable size="small" onClick={() => acqHandleCard('acc-counterparties')} style={{ cursor: 'pointer' }}>
            <Statistic
              title="Прострочена"
              value={acqKpi.overdueReceivables}
              precision={0}
              prefix={<WarningOutlined />}
              suffix="UAH"
              valueStyle={{ color: acqKpi.overdueReceivables > 0 ? ACQ_COLOR_DANGER : ACQ_COLOR_WARNING }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              Поки заглушка (потрібен термін)
            </div>
          </Card>
        </Col>
      </Row>

      {/* Графік + recent ops */}
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={14}>
          <Card
            size="small"
            title="Динаміка виручки"
            extra={<Typography.Text type="secondary" style={{ fontSize: 11 }}>за {acqMonths} міс</Typography.Text>}
          >
            {acqTrendQ.isLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
            ) : (acqTrendQ.data ?? []).every((p) => p.revenue === 0) ? (
              <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Empty description="Немає продажів за період" />
              </div>
            ) : (
              <ReactECharts option={acqChartOption} style={{ height: 220 }} notMerge />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card size="small" title="Останні операції">
            <List
              size="small"
              loading={acqOpsQ.isLoading}
              dataSource={acqOpsQ.data ?? []}
              locale={{ emptyText: <Empty description="Немає проводок" /> }}
              renderItem={(acqOp) => {
                const acqIsRevenue = acqOp.creditAccount === '703';
                const acqLabel = acqOp.documentNumber
                  ? `${acqOp.documentTypeName ?? acqOp.documentNumber} №${acqOp.documentNumber}`
                  : `${acqOp.debitAccount}/${acqOp.creditAccount}`;
                return (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Space size={6}>
                          <Typography.Text style={{ fontSize: 13 }}>{acqLabel}</Typography.Text>
                          <Tag color="default" style={{ fontSize: 10 }}>{dayjs(acqOp.entryDate).format('DD.MM')}</Tag>
                        </Space>
                      }
                      description={
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                          {acqOp.description ?? `Дт ${acqOp.debitAccount} / Кт ${acqOp.creditAccount}`}
                        </Typography.Text>
                      }
                    />
                    <span style={{
                      color: acqIsRevenue ? ACQ_COLOR_SUCCESS : ACQ_COLOR_DANGER,
                      fontWeight: 500,
                      fontSize: 13,
                    }}>
                      {acqIsRevenue ? '+' : '–'} ₴ {Math.abs(acqOp.amount).toLocaleString('uk-UA')}
                    </span>
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>
      </Row>
    </Space>
  );
}
